
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import * as Sentry from '@sentry/react';
import { captureException } from '@/integrations/sentry/client';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  // Whether to report errors to Sentry
  reportToSentry?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a chunk loading error - if so, handle it immediately
    const errorString = error?.message || error?.name || String(error) || "";
    const isChunkError =
      errorString.includes("Loading chunk") ||
      errorString.includes("ChunkLoadError") ||
      errorString.includes("_next/static/chunks") ||
      error.name === "ChunkLoadError" ||
      (errorString.includes("Failed to fetch") && errorString.includes("chunk"));

    if (isChunkError) {
      // Handle chunk error by clearing cache and reloading
      if (typeof window !== "undefined") {
        // Clear all caches
        if ("caches" in window) {
          caches.keys().then((cacheNames) => {
            return Promise.all(
              cacheNames.map((cacheName) => caches.delete(cacheName))
            );
          });
        }

        // Unregister service workers
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => {
              registration.unregister();
            });
          });
        }

        // Reload the page to fetch fresh chunks
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
      // Return no error state so the boundary doesn't render the error UI
      return { hasError: false, error: null };
    }

    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is a chunk loading error - if so, don't report to Sentry
    const errorString = error?.message || error?.name || String(error) || "";
    const isChunkError =
      errorString.includes("Loading chunk") ||
      errorString.includes("ChunkLoadError") ||
      errorString.includes("_next/static/chunks") ||
      error.name === "ChunkLoadError" ||
      (errorString.includes("Failed to fetch") && errorString.includes("chunk"));

    if (isChunkError) {
      // Chunk errors are handled in getDerivedStateFromError, don't log or report
      return;
    }

    console.error("Component Error:", error);
    console.error("Error Details:", errorInfo);
    
    // Report to Sentry if enabled
    if (this.props.reportToSentry !== false) {
      captureException(error, {
        errorInfo,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <div>
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription className="space-y-2">
              <div>
                An error occurred while displaying this content.
                {this.state.error && (
                  <div className="text-xs mt-1 opacity-80">
                    {this.state.error.message}
                  </div>
                )}
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={this.handleReset}
                >
                  <RefreshCcw className="mr-2 h-3 w-3" /> Try Again
                </Button>
              </div>
            </AlertDescription>
          </div>
        </Alert>
      );
    }

    return this.props.children;
  }
}

// Export a Sentry wrapped version as the default
export default Sentry.withErrorBoundary(ErrorBoundary, {
  fallback: (props) => (
    <Alert variant="destructive" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <div>
        <AlertTitle>Application Error</AlertTitle>
        <AlertDescription className="space-y-2">
          <div>
            A critical error has occurred and has been reported to our team.
          </div>
          <div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={props.resetError}
            >
              <RefreshCcw className="mr-2 h-3 w-3" /> Try Again
            </Button>
          </div>
        </AlertDescription>
      </div>
    </Alert>
  ),
});

// Also export the unwrapped component for cases where Sentry wrapping is not desired
export { ErrorBoundary as ErrorBoundaryBase };
